import {ssrSafeDocument} from '@github-ui/ssr-utils'

/**
 * Tracks pointer exits for any number of elements with a single document-level pointer listener.
 * Each observer gets notified when the pointer moves outside of its element.
 * Scroll events also trigger pointer exit callbacks to clear hover state when scrolling.
 */
type PointerExitObserver = {
  element: HTMLElement
  shouldIgnore?: () => boolean
  onPointerExit: () => void
}

type TrackedPointerExitObserver = PointerExitObserver & {pendingExit: boolean}

const observers = new Set<TrackedPointerExitObserver>()
let isListening = false

/**
 * Process observers and notify them of pointer exit.
 * @param isPointerInElement - Optional function to check if pointer is inside an element.
 *                             If not provided, assumes pointer is outside all elements (e.g., for scroll events).
 */
const processObservers = (isPointerInElement?: (element: HTMLElement) => boolean) => {
  for (const observer of observers) {
    const {element, shouldIgnore, onPointerExit} = observer
    if (!element.isConnected) {
      observers.delete(observer)
      continue
    }

    if (isPointerInElement?.(element)) {
      observer.pendingExit = false
      continue
    }

    observer.pendingExit = true

    if (shouldIgnore?.()) {
      continue
    }

    observer.pendingExit = false
    onPointerExit()
  }

  if (observers.size === 0) {
    teardownListener()
  }
}

const handlePointerMove = (event: PointerEvent) => {
  const target = event.target instanceof Node ? event.target : null
  processObservers(element => !!(target && element.contains(target)))
}

const handleScroll = () => {
  processObservers()
}

const ensureListener = () => {
  if (isListening || !ssrSafeDocument) return
  ssrSafeDocument.addEventListener('pointermove', handlePointerMove)
  // eslint-disable-next-line github/prefer-observers
  ssrSafeDocument.addEventListener('scroll', handleScroll, true)
  isListening = true
}

const teardownListener = () => {
  if (!isListening || !ssrSafeDocument || observers.size > 0) return
  ssrSafeDocument.removeEventListener('pointermove', handlePointerMove)
  ssrSafeDocument.removeEventListener('scroll', handleScroll, true)
  isListening = false
}

export function registerPointerExitObserver(observer: PointerExitObserver) {
  const {element} = observer
  if (!element) return () => {}

  const trackedObserver: TrackedPointerExitObserver = {...observer, pendingExit: false}
  observers.add(trackedObserver)
  ensureListener()

  return () => {
    observers.delete(trackedObserver)
    teardownListener()
  }
}
